import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { debounce } from 'es-toolkit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CategoryAd } from '@/components/Ads/CategoryAd';
import EnsureLoggedInContainer from '@/components/EnsureLoggedIn/EnsureLoggedInContainer';
import { InboxFolderStats } from '@/components/InboxMessenger/components/Folders/types/InboxFolderStats';
import { InboxLayout, Messenger } from '@/components/InboxMessenger/InboxMessenger';
import { normalizeQuery } from '@/components/InboxMessenger/utils/urls';
import PageTitle from '@/components/PageTitle/PageTitle';
import { usePreviousLocation } from '@/components/PreviousLocation/PreviousLocation';
import useWindowSize from '@/hooks/useWindowSize';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { LeaderBoardAdTypes } from '@/redux/modules/catalog/ad/catalogAd.types';
import { getCatalogStatus } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getBaseUrl } from '@/redux/modules/config';
import { getConversation } from '@/redux/modules/conversation/conversations/conversation.selectors';
import {
    archiveConversationFolder,
    createConversationFolder,
    renameConversationFolder,
} from '@/redux/modules/conversation/folders/conversationFolders.actions';
import { getConversationFolders } from '@/redux/modules/conversation/folders/conversationFolders.selectors';
import {
    addAttachment,
    clearDraft,
    deleteAttachment,
    sendConversationMessage,
} from '@/redux/modules/conversation/messages/conversationMessages.actions';
import {
    getConversationMessages,
    getDraftId,
} from '@/redux/modules/conversation/messages/conversationMessages.selectors';
import { loadConversationPageData } from '@/redux/modules/conversation/page/conversationPage.actions';
import { Folder } from '@/types/Messaging';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { parseQueryParams } from '@/utils/queryParams';
import { getMessagesUrl, toQueryString } from '@/utils/urls';

import { Context } from './Context';
import MessagesPageHeaderMeta from './MessagesPageHeaderMeta';

const MessengerPage = () => {
    const baseUrl = useAppSelector(getBaseUrl);
    const { conversationId: conversationIdString } = useParams<{
        conversationId: string;
    }>();
    const conversationId = Number(conversationIdString);
    const { catalogId, sender, subject } = useAppSelector((state) => getConversation(state, conversationId));
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const messages = useAppSelector((state) => getConversationMessages(state, conversationId));
    const inboxFolders: InboxFolderStats = useAppSelector(getConversationFolders);
    const { width } = useWindowSize();
    const draftId = useAppSelector((state) => getDraftId(state, conversationId));
    const { formatNumber } = useIntl();
    const formatMessage = useAppFormatMessage();

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const queryParams = new URLSearchParams(location.search);
    const activeFolder = queryParams.get('folderId') || 0;
    const params: any = parseQueryParams(location.search);

    const prevRouterLocation = usePreviousLocation();
    const onBack = () => {
        if (prevRouterLocation && location?.key !== prevRouterLocation?.key) {
            return navigate(-1);
        }
        navigate({ pathname: getMessagesUrl(), search: location.search });
    };

    const navigateTo = (param: any) => {
        const newParams = normalizeQuery({ ...params, ...param });
        navigate({
            pathname: getMessagesUrl(),
            search: toQueryString(newParams),
        });
    };

    const setActiveInbox = useCallback(
        (folderId) => {
            const newParams = normalizeQuery({
                ...params,
                folderId,
                page: 1,
                search: '',
            });
            navigate({
                pathname: getMessagesUrl(),
                search: toQueryString(newParams),
            });
        },
        [params, navigate]
    );

    const onSendMessage = (message) =>
        dispatch(
            sendConversationMessage({
                conversationId,
                draftId,
                messageBody: message,
            })
        );

    const onAddAttachment = (message, attachedFiles) =>
        dispatch(
            addAttachment({
                attachments: attachedFiles,
                conversationId,
                draftId,
                messageBody: message,
            })
        );

    const onDeleteAttachment = (attachmentName) => dispatch(deleteAttachment({ attachmentName, conversationId }));

    useEffect(() => {
        dispatch(clearDraft(conversationId));
    }, []); // eslint-disable-line

    const folders = useMemo(
        () => [...inboxFolders.systemFolders, ...inboxFolders.userFolders],
        [inboxFolders.systemFolders, inboxFolders.userFolders]
    );

    let numberActiveFolder = null;
    if (!params.filter) {
        numberActiveFolder = Number(activeFolder);
    }

    const archiveConvoFolder = useCallback(
        (folderId: number) => dispatch(archiveConversationFolder(folderId)),
        [dispatch]
    );

    const createConvoFolder = useCallback((name: string) => dispatch(createConversationFolder(name)), [dispatch]);

    const removeConvoFolder = useCallback(
        (folderId: number, name: string) => dispatch(renameConversationFolder({ folderId, name })),
        [dispatch]
    );

    const archiveConvoAction = useCallback(
        () =>
            dispatch(
                loadConversationPageData({
                    action: 'archive',
                    conversationIds: [conversationId],
                    query: params,
                })
            ).then(() => setActiveInbox(0)),
        [conversationId, dispatch, params, setActiveInbox]
    );

    const markReadConvoAction = useCallback(
        () =>
            dispatch(
                loadConversationPageData({
                    action: 'markRead',
                    conversationIds: [conversationId],
                    query: params,
                })
            ),
        [conversationId, dispatch, params]
    );

    const markUnreadConvoAction = useCallback(
        () =>
            dispatch(
                loadConversationPageData({
                    action: 'markUnread',
                    conversationIds: [conversationId],
                    query: params,
                })
            ),
        [conversationId, dispatch, params]
    );

    const moveConvoAction = useCallback(
        (folder: Folder) => {
            const { id: folderId, name: folderName } = folder;
            toast('success', {
                description: formatMessage(['toastMessages', 'descriptions', 'conversationMovedToFolder'], {
                    values: { folderName },
                }),
                title: formatMessage(['toastMessages', 'titles', 'success']) as unknown as string,
            });
            dispatch(
                loadConversationPageData({
                    action: 'move',
                    conversationIds: [conversationId],
                    folderId,
                    markOnly: false,
                    query: { ...params },
                })
            );
            return setActiveInbox(activeFolder);
        },
        [activeFolder, conversationId, dispatch, formatMessage, params, setActiveInbox]
    );

    const adId = 'messages';
    const adPageName = `leaderboard-${adId}`;

    // Prevent Messenger Page from trying to load SSR
    const [hasRendered, setRendered] = useState(false);
    useEffect(() => setRendered(true), [hasRendered]);

    if (!hasRendered) {
        return null;
    }

    return (
        <EnsureLoggedInContainer>
            <MessagesPageHeaderMeta baseUrl={baseUrl} />
            <CategoryAd
                adType={LeaderBoardAdTypes[adId]}
                pageName={adPageName}
            />
            <PageTitle
                className="mx-auto mt-lg mb-md w-full max-w-[1240px] px-md print:ml-[7.5px]"
                title={<>{formatMessage(['messaging', 'title'])}</>}
            />
            <InboxLayout
                activeFolder={numberActiveFolder}
                className="px-md"
                context={<Context conversationId={conversationId} />}
                formatNumber={formatNumber}
                hideContext={width < 768}
                hideLeftNav={width < 1024}
                inboxFolders={inboxFolders}
                loading={false}
                onArchiveFolder={archiveConvoFolder}
                onCreateFolder={createConvoFolder}
                onRenameFolder={removeConvoFolder}
                onSearch={debounce((search: string) => navigateTo({ search }), 250)}
                onSetActiveInbox={setActiveInbox}
            >
                <Messenger
                    activeFolder={numberActiveFolder}
                    archiveLabel={formatMessage(['messaging', 'archive'])}
                    catalogIsOnlineOrLive={catalogStatus === 'online' || catalogStatus === 'live'}
                    folders={folders}
                    houseName={sender}
                    markReadLabel={formatMessage(['messaging', 'markRead'])}
                    markUnreadLabel={formatMessage(['messaging', 'markUnread'])}
                    messages={messages}
                    moveLabel={formatMessage(['messaging', 'move'])}
                    onAddAttachment={onAddAttachment}
                    onArchive={archiveConvoAction}
                    onBack={onBack}
                    onDeleteAttachment={onDeleteAttachment}
                    onMarkRead={() => {
                        return markReadConvoAction;
                    }}
                    onMarkUnread={markUnreadConvoAction}
                    onMove={moveConvoAction}
                    onSendMessage={onSendMessage}
                    subject={subject}
                />
            </InboxLayout>
        </EnsureLoggedInContainer>
    );
};

export default MessengerPage;
